/*
  Google Analytics 4 events from the theme.

  The theme does not load gtag itself: events are published to Shopify's Customer events
  (Shopify.analytics.publish) under the "cg:" prefix, and the "Google Analytics 4" custom pixel
  (analytics/custom-pixel.js, Settings > Customer events) forwards them to GA4 together with the
  standard e-commerce events (view_item, add_to_cart, checkout, purchase...).
  The "Meta Pixel" custom pixel (analytics/meta-pixel.js) maps some of them to Meta events (Contact, Lead...).
*/
(() => {
  const EVENT_PREFIX = 'cg:';
  const PENDING_AUTH_KEY = 'cosmogen:analytics-pending-auth';
  const MAX_PARAM_LENGTH = 100;

  const script = document.currentScript;
  const isCustomerLoggedIn = script?.dataset.customer === 'true';
  const currency = window.Shopify?.currency?.active;
  const queue = [];

  function publish(name, params) {
    const analytics = window.Shopify?.analytics;
    if (typeof analytics?.publish !== 'function') return false;
    analytics.publish(EVENT_PREFIX + name, params);
    return true;
  }

  function track(name, params = {}) {
    if (window.Shopify?.designMode) return;
    if (!publish(name, params)) queue.push([name, params]);
  }

  // Shopify.analytics may appear after this script runs: flush the queue once the page has loaded.
  window.addEventListener('load', () => {
    while (queue.length && publish(...queue[0])) queue.shift();
  });

  const clip = (value) => (value || '').replace(/\s+/g, ' ').trim().slice(0, MAX_PARAM_LENGTH);

  function linkText(link) {
    return clip(link.textContent) || clip(link.getAttribute('aria-label')) || clip(link.querySelector('img')?.alt);
  }

  /* Product lists: view_item_list when a list scrolls into view, select_item on a card click */

  // data-cg-* attributes come from snippets/analytics-item-attrs.liquid.
  function itemFromData(data) {
    const item = {
      item_id: data.cgItemId,
      item_name: data.cgItemName,
      item_brand: data.cgItemBrand || undefined,
      item_category: data.cgItemCategory || undefined,
      item_variant: data.cgItemVariant === 'Default Title' ? undefined : data.cgItemVariant,
      price: Number(data.cgItemPrice),
      quantity: 1,
    };
    if (data.cgItemDiscount) item.discount = Number(data.cgItemDiscount);
    return item;
  }

  function itemFromCard(card, list, index) {
    return { ...itemFromData(card.dataset), item_list_name: list.dataset.cgList, index };
  }

  const listCards = (list) => [...list.querySelectorAll('.card-wrapper[data-cg-item-id]')];

  const listObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        listObserver.unobserve(entry.target);
        const list = entry.target;
        const items = listCards(list).map((card, index) => itemFromCard(card, list, index));
        if (!items.length) return;
        track('view_item_list', { item_list_name: list.dataset.cgList, currency, items });
      });
    },
    { threshold: 0.2 }
  );

  function observeLists(root) {
    if (root.matches?.('[data-cg-list]')) listObserver.observe(root);
    root.querySelectorAll?.('[data-cg-list]').forEach((list) => listObserver.observe(list));
  }

  /* Product page: view_item is published here and not by the pixels, because the pixels only see
     Shopify's variant.price — the price before the discount the page shows and Shopify Discounts
     apply at checkout. The attributes carry the price the customer sees. */

  function trackProductView() {
    const element = document.querySelector('[data-cg-product-view]');
    if (!element) return;
    const item = itemFromData(element.dataset);
    track('view_item', {
      currency,
      value: item.price,
      variant_id: element.dataset.cgItemVariantId,
      items: [item],
    });
  }

  /* Promotions: the hero banner is a promotion shown on the home page */

  const promotionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        promotionObserver.unobserve(entry.target);
        track('view_promotion', promotionParams(entry.target));
      });
    },
    { threshold: 0.5 }
  );

  // Every hero banner slide is its own promotion; the marquee bar is one.
  function promotionParams(element) {
    const slide = element.closest('.hero-banner__slide');
    const link = element.closest('a') || element.querySelector('a');
    const creative = link ? linkText(link) || clip(link.getAttribute('href')) : undefined;
    if (slide) {
      return {
        promotion_id: slide.dataset.cgPromotionId,
        promotion_name: clip(slide.dataset.cgPromotionName),
        creative_slot: 'hero_banner',
        creative_name: creative,
      };
    }
    return { promotion_id: 'marquee_bar', promotion_name: 'Marquee bar', creative_name: creative };
  }

  /* Clicks */

  const CHAT_METHODS = ['Facebook', 'Instagram', 'WhatsApp', 'LINE'];
  const MESSENGER_HOSTS = {
    whatsapp: /^(wa\.me|api\.whatsapp\.com|chat\.whatsapp\.com)$/,
    line: /^(line\.me|lin\.ee)$/,
    facebook_messenger: /^(m\.me|messenger\.com)$/,
  };
  const SOCIAL_HOSTS = {
    facebook: /(^|\.)facebook\.com$|^fb\.com$/,
    instagram: /(^|\.)instagram\.com$/,
    tiktok: /(^|\.)tiktok\.com$/,
    youtube: /(^|\.)youtube\.com$|^youtu\.be$/,
  };
  const MAP_LINK =/google\.[a-z.]+\/maps|maps\.google\.|maps\.app\.goo\.gl|goo\.gl\/maps/i;

  function onButtonClick(button) {
    const chatWidget = button.closest('.chat-widget');
    if (chatWidget && button.classList.contains('chat-widget__toggle')) {
      // Capture phase: the widget has not toggled yet, so "closed" means it is about to open.
      if (!chatWidget.classList.contains('is-open')) track('chat_open', { location: 'chat_widget' });
      return;
    }

    if (button.classList.contains('pickup-availability-button')) {
      track('pickup_info_view', { location: 'product' });
      return;
    }

    if (button.classList.contains('gift-card__copy-button')) {
      track('promo_code_copy', { location: 'gift_card_page' });
    }
  }

  function onClick(event) {
    const button = event.target.closest('button');
    if (button) {
      onButtonClick(button);
      return;
    }

    const link = event.target.closest('a[href]');

    const card = event.target.closest('.card-wrapper[data-cg-item-id]');
    const list = card?.closest('[data-cg-list]');
    if (card && list && link) {
      const index = listCards(list).indexOf(card);
      track('select_item', {
        item_list_name: list.dataset.cgList,
        currency,
        items: [itemFromCard(card, list, index)],
      });
      return;
    }

    if (!link) return;
    const href = link.getAttribute('href');

    const chatLink = link.closest('.chat-widget__link');
    if (chatLink) {
      const label = clip(chatLink.querySelector('.chat-widget__label')?.textContent);
      track('contact_click', { method: CHAT_METHODS.includes(label) ? label.toLowerCase() : label, location: 'chat_widget' });
      return;
    }

    const liveVideoLink = link.closest('.live-video-request__link');
    if (liveVideoLink) {
      track('contact_click', {
        method: liveVideoLink.dataset.method,
        location: 'live_video',
        item_name: clip(document.querySelector('.product__title h1')?.textContent),
      });
      return;
    }

    if (href.startsWith('tel:') || href.startsWith('mailto:')) {
      track('contact_click', {
        method: href.startsWith('tel:') ? 'phone' : 'email',
        location: link.closest('.footer') ? 'footer' : 'page',
      });
      return;
    }

    if (link.classList.contains('contact-info-map__button') || MAP_LINK.test(href)) {
      track('contact_click', { method: 'map', location: link.closest('.pickup-availability-list') ? 'pickup' : 'page' });
      return;
    }

    if (link.closest('.hero-banner, .marquee-bar')) {
      track('select_promotion', promotionParams(link));
      // A slide may lead to the LINE chat: that is also a contact, like the chat buttons.
      if (link.closest('.hero-banner__slide')?.dataset.cgPromotionLink === 'line') {
        track('contact_click', { method: 'line', location: 'hero_banner' });
      }
      return;
    }

    if (link.closest('.gift-cards__amounts, .gift-cards__cta')) {
      track('select_content', {
        content_type: 'gift_card',
        item_id: clip(link.querySelector('.gift-cards__card-value')?.textContent) || 'gift_card_product',
      });
      return;
    }

    if (link.classList.contains('gift_card__apple-wallet')) {
      track('add_to_wallet', { location: 'gift_card_page' });
      return;
    }

    const predictiveSearch = link.closest('predictive-search');
    if (predictiveSearch) {
      track('search', {
        search_term: clip(predictiveSearch.querySelector('input[type="search"]')?.value),
        search_type: 'predictive',
        link_url: clip(href),
      });
      return;
    }

    // Link from the "earn bonuses" hint on a product to the bonus programme page.
    if (link.classList.contains('bonus-earn__link')) {
      track('bonus_hint_click', { location: document.querySelector('product-info') ? 'product' : 'page' });
      return;
    }

    // Messenger and social links outside the chat widget (e.g. footer menu items).
    const linkHost = link.hostname.replace(/^www\./, '');
    const linkLocation = link.closest('.footer') ? 'footer' : link.closest('.section-header') ? 'header' : 'page';
    const messenger = Object.entries(MESSENGER_HOSTS).find(([, pattern]) => pattern.test(linkHost))?.[0];
    if (messenger) {
      track('contact_click', { method: messenger, location: linkLocation });
      return;
    }
    const network = Object.entries(SOCIAL_HOSTS).find(([, pattern]) => pattern.test(linkHost))?.[0];
    if (network) {
      track('social_click', { method: network, location: linkLocation, link_url: clip(link.href) });
      return;
    }

    const collectionCard = link.closest('.collection-list__item');
    if (collectionCard) {
      track('select_content', {
        content_type: 'collection',
        item_id: clip(new URL(link.href).pathname),
        link_text: linkText(collectionCard.querySelector('.card__heading') || link),
      });
      return;
    }

    const menuLocation = link.closest('.section-header') ? 'header' : link.closest('.footer') ? 'footer' : null;
    if (menuLocation) {
      track('menu_click', { menu_location: menuLocation, link_text: linkText(link), link_url: clip(href) });
      return;
    }

    // Replaces GA's automatic outbound click tracking, which cannot see the page from the pixel sandbox.
    const url = new URL(link.href, window.location.href);
    if (/^https?:$/.test(url.protocol) && url.hostname !== window.location.hostname) {
      track('click', { outbound: true, link_domain: url.hostname, link_url: clip(url.href), link_text: linkText(link) });
    }
  }

  /* Engagement time and scroll depth.
     The pixel iframe never has focus, so GA cannot measure engagement itself: the theme counts the time
     the page is visible and focused and reports it as user_engagement (engagement_time_msec). */

  // Раз в минуту: сумма времени та же, а событий в отчётах вчетверо меньше.
  const ENGAGEMENT_FLUSH_MS = 60000;
  let engagedMs = 0;
  let activeSince = null;

  const isActive = () => document.visibilityState === 'visible' && document.hasFocus();

  function updateEngagement() {
    const now = Date.now();
    if (activeSince !== null) engagedMs += now - activeSince;
    activeSince = isActive() ? now : null;
  }

  function flushEngagement() {
    updateEngagement();
    if (engagedMs < 1000) return;
    track('user_engagement', { engagement_time_msec: Math.round(engagedMs) });
    engagedMs = 0;
  }

  function trackEngagement() {
    activeSince = isActive() ? Date.now() : null;
    ['focus', 'blur'].forEach((type) => window.addEventListener(type, updateEngagement));
    document.addEventListener('visibilitychange', () =>
      document.visibilityState === 'hidden' ? flushEngagement() : updateEngagement()
    );
    window.addEventListener('pagehide', flushEngagement);
    setInterval(() => activeSince !== null && flushEngagement(), ENGAGEMENT_FLUSH_MS);
  }

  function trackScroll() {
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0 || window.scrollY / scrollable < 0.9) return;
      window.removeEventListener('scroll', onScroll);
      track('scroll', { percent_scrolled: 90 });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* Forms whose success is only known after Shopify redirects back */

  function trackPostedForms() {
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;

    // Shopify marks every customer form as posted, so the hash tells the popup and the footer apart.
    const customerPosted =
      params.get('customer_posted') === 'true' ||
      Boolean(document.querySelector('[data-popup-success], .newsletter-form__message--success'));
    if (customerPosted) {
      const method = hash === '#SubscribePopupForm' ? 'newsletter_popup' : 'newsletter_footer';
      track('sign_up', { method });
    }

    if (params.get('contact_posted') === 'true') {
      track('generate_lead', { lead_source: 'contact_form', currency, value: 0 });
    }
  }

  // Login and registration succeed only if the next page is rendered for a logged-in customer.
  function trackAuth() {
    let pending = null;
    try {
      pending = sessionStorage.getItem(PENDING_AUTH_KEY);
      sessionStorage.removeItem(PENDING_AUTH_KEY);
    } catch (error) {
      return;
    }
    if (!pending || !isCustomerLoggedIn) return;
    track(pending, { method: 'email' });
  }

  function onSubmit(event) {
    const form = event.target;

    // Pre-order: the buy button adds a "Pre-order" line item property (snippets/buy-buttons.liquid).
    // The cart events from Shopify do not carry it, so the theme reports the pre-order separately.
    if (form.action?.includes('/cart/add') && form.querySelector('[id^="Preorder-"] input[name^="properties"]')) {
      track('preorder_add_to_cart', {
        item_name: clip((document.querySelector('.product__title h1') || document.querySelector('h1'))?.textContent),
        item_variant: form.querySelector('.product-variant-id')?.value,
        currency,
      });
    }

    const formType = form.querySelector('input[name="form_type"]')?.value;
    const pendingEvent = { customer_login: 'login', create_customer: 'sign_up' }[formType];
    if (!pendingEvent) return;
    try {
      sessionStorage.setItem(PENDING_AUTH_KEY, pendingEvent);
    } catch (error) {
      // Storage blocked: the event is simply not tracked.
    }
  }

  /* Collection filters and sorting (assets/facets.js updates the URL on every change) */

  function trackFilters() {
    const facets = customElements.get('facet-filters-form');
    if (!facets || facets.cgTracked) return;
    facets.cgTracked = true;
    const updateURLHash = facets.updateURLHash;
    facets.updateURLHash = function (searchParams) {
      let filterParams = (searchParams || '').replace(/\+/g, ' ');
      try {
        filterParams = decodeURIComponent(filterParams);
      } catch (error) {
        // Keep the encoded string.
      }
      track('filter_apply', { filter_params: clip(filterParams) || '(none)' });
      return updateURLHash.apply(this, arguments);
    };
  }

  /* Wishlist (assets/wishlist.js): the button sits inside a card or on the product page, both carry the
     item attributes; a change made in another tab has no button and is not an action of this page. */

  document.addEventListener('cg:wishlist', ({ detail }) => {
    if (!detail || detail.added === undefined || !detail.element) return;
    const holder = detail.element.closest('[data-cg-item-id]') || document.querySelector('[data-cg-product-view]');
    if (!holder) return;
    const item = itemFromData(holder.dataset);
    track(detail.added ? 'add_to_wishlist' : 'remove_from_wishlist', {
      currency,
      value: item.price,
      location: detail.source || undefined,
      items: [item],
    });
  });

  /* Theme components report their own actions via this global */

  window.cosmogenAnalytics = { track };

  document.addEventListener('click', onClick, true);
  document.addEventListener('submit', onSubmit, true);

  document.addEventListener('DOMContentLoaded', () => {
    observeLists(document);
    // Slides come and go as the slider rotates; the clones it adds are aria-hidden and stay untracked.
    document
      .querySelectorAll('.hero-banner__slide:not([aria-hidden="true"])')
      .forEach((promotion) => promotionObserver.observe(promotion));

    // Lists rendered later: related products, filtered collection grid.
    new MutationObserver((mutations) =>
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => node.nodeType === 1 && observeLists(node)))
    ).observe(document.body, { childList: true, subtree: true });

    trackProductView();
    trackFilters();
    trackPostedForms();
    trackAuth();
    trackEngagement();
    trackScroll();
  });
})();
